// src/pages/Admin/Casting/CastingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

import CastingHeader from "./components/CastingHeader";
import MatsSidebar from "./components/MatsSidebar";
import MatEditor from "./components/MatEditor";
import MessagesPanel from "./components/MessagesPanel";
import CastingScreenPreview from "./components/CastingScreenPreview";

import {
  getEventCastBundle,
  upsertEventMatWithSlots,
  toggleEventMatOpen,
  upsertEventCastState,
  createEventCastMessage,
  updateEventCastMessage,
  toggleEventCastMessageActive,
  deleteEventCastMessage,
} from "./../../services/eventCastServices";

const FALLBACK_EVENT = (eventId) => {
  try {
    const raw = localStorage.getItem(`casting_event_${eventId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const initMatForm = (selectedMat = 1, mat = null) => ({
  label: mat?.label || `TAPIS ${selectedMat}`,
  is_open: mat?.is_open !== undefined ? Boolean(mat.is_open) : true,
  sort_order: mat?.sort_order ?? Number(selectedMat),
  slots: [1, 2, 3].map((i) => {
    const found = (mat?.Slots || []).find((s) => Number(s.slot_index) === i);
    return {
      slot_index: i,
      label: found?.label || "",
      note: found?.note || "",
      is_active:
        found?.is_active !== undefined ? Boolean(found.is_active) : true,
    };
  }),
});

export default function CastingPage() {
  useScrollToTop();
  const { eventId } = useParams();

  const [loading, setLoading] = useState(true);
  const [savingMat, setSavingMat] = useState(false);

  const [event, setEvent] = useState(() => FALLBACK_EVENT(eventId));
  const [mats, setMats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [castState, setCastState] = useState(null);
  const [isScreen, setIsScreen] = useState(false);

  // panneau actif : "mats" | "messages"
  const [panel, setPanel] = useState("mats");

  // tapis sélectionné
  const [selectedMat, setSelectedMat] = useState(1);
  const selectedMatData = useMemo(
    () =>
      mats.find((m) => Number(m.mat_number) === Number(selectedMat)) || null,
    [mats, selectedMat]
  );

  const [matForm, setMatForm] = useState(() => initMatForm(1, null));

  const hydrateMatForm = (mat) => setMatForm(initMatForm(selectedMat, mat));

  const fetchBundle = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await getEventCastBundle(eventId);
      if (!data) return;

      setEvent(data.event || FALLBACK_EVENT(eventId));
      setMats(Array.isArray(data.mats) ? data.mats : []);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setCastState(data.state || null);

      const mat = (Array.isArray(data.mats) ? data.mats : []).find(
        (m) => Number(m.mat_number) === Number(selectedMat)
      );
      hydrateMatForm(mat);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des données de casting.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Refresh léger dédié à l'écran (ne touche pas loading / matForm)
  const refreshScreenBundle = async () => {
    if (!eventId) return;

    try {
      const data = await getEventCastBundle(eventId);
      if (!data) return;

      setEvent(data.event || FALLBACK_EVENT(eventId));
      setMats(Array.isArray(data.mats) ? data.mats : []);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setCastState(data.state || null);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour de l’écran.");
    }
  };

  useEffect(() => {
    fetchBundle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    hydrateMatForm(selectedMatData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMat, selectedMatData?.id]);

  const saveMat = async () => {
    if (!eventId) return;

    const slots = (matForm.slots || [])
      .map((s) => ({
        slot_index: Number(s.slot_index),
        label: String(s.label || "").trim(),
        note: String(s.note || "").trim(),
        is_active: s.is_active !== undefined ? Boolean(s.is_active) : true,
      }))
      .filter((s) => s.label);

    setSavingMat(true);
    try {
      const payload = {
        label: matForm.label,
        is_open: Boolean(matForm.is_open), // ✅ tu peux préciser ouvert/fermé ici
        sort_order: Number(matForm.sort_order || selectedMat),
        slots,
      };

      const saved = await upsertEventMatWithSlots(
        eventId,
        selectedMat,
        payload
      );
      if (!saved) return;

      toast.success("Tapis enregistré.");

      setMats((prev) => {
        const copy = [...(prev || [])];
        const idx = copy.findIndex(
          (m) => Number(m.mat_number) === Number(selectedMat)
        );

        const normalized = {
          ...saved,
          mat_number: Number(saved.mat_number ?? selectedMat),
          Slots: Array.isArray(saved.Slots) ? saved.Slots : saved?.Slots || [],
        };

        if (idx >= 0) copy[idx] = normalized;
        else copy.push(normalized);

        return copy.sort(
          (a, b) =>
            (a.sort_order ?? a.mat_number) - (b.sort_order ?? b.mat_number)
        );
      });
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'enregistrement du tapis.");
    } finally {
      setSavingMat(false);
    }
  };

  const toggleMat = async () => {
    if (!eventId) return;
    try {
      const r = await toggleEventMatOpen(eventId, selectedMat);
      if (!r) return;

      setMatForm((s) => ({ ...s, is_open: Boolean(r.is_open) }));
      setMats((prev) =>
        (prev || []).map((m) =>
          Number(m.mat_number) === Number(selectedMat)
            ? { ...m, is_open: Boolean(r.is_open) }
            : m
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du changement d'état du tapis.");
    }
  };

  const setMode = async (mode) => {
    if (!eventId) return;
    try {
      const updated = await upsertEventCastState(eventId, {
        mode,
        active_message_id: castState?.active_message_id ?? null,
      });
      if (!updated) return;
      setCastState(updated);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de mettre à jour le mode.");
    }
  };

  // 3) handler (remplace openDisplay)
  const toggleScreen = () => setIsScreen((v) => !v);

  if (!eventId) {
    return (
      <div className="p-6">
        <p className="text-red-600">eventId manquant dans l’URL.</p>
      </div>
    );
  }

  return (
    <>
      {isScreen ? (
        <CastingScreenPreview
          event={event}
          castState={castState}
          mats={mats}
          messages={messages}
          onClose={() => setIsScreen(false)}
          onRefresh={refreshScreenBundle} // ✅ AJOUT ICI
          onSetMode={setMode}
        />
      ) : (
        <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
          <div className="mx-auto w-full max-w-6xl px-3 sm:px-6 pt-[130px] md:pt-[150px] pb-10">
            <ScrollIndicator />

            <div className="mb-6 rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 shadow-sm px-4 sm:px-6 py-5">
              <p className="text-xs tracking-[0.35em] uppercase text-slate-500 dark:text-slate-400 text-center">
                Administrateurs
              </p>
              <h2 className="mt-2 text-center text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-wide font-montserrat uppercase">
                Informations écran compétition
              </h2>
            </div>

            <CastingHeader
              event={event}
              eventId={eventId}
              panel={panel}
              setPanel={setPanel}
              castState={castState}
              onRefresh={fetchBundle}
              onToggleScreen={toggleScreen}
              onSetMode={setMode}
              isScreen={isScreen}
            />

            {/* PANNEAUX */}
            {panel === "mats" ? (
              <div className="mt-4 grid lg:grid-cols-3 gap-4">
                <aside className="lg:col-span-1">
                  <MatsSidebar
                    mats={mats}
                    selectedMat={selectedMat}
                    onSelectMat={setSelectedMat}
                    currentLabel={matForm.label || `TAPIS ${selectedMat}`}
                    isOpen={Boolean(matForm.is_open)}
                    onToggleCurrentMat={toggleMat}
                  />
                </aside>

                <section className="lg:col-span-2">
                  <MatEditor
                    loading={loading}
                    saving={savingMat}
                    selectedMat={selectedMat}
                    form={matForm}
                    setForm={setMatForm}
                    onSave={saveMat}
                  />
                </section>
              </div>
            ) : (
              <div className="mt-4">
                <MessagesPanel
                  eventId={eventId}
                  messages={messages}
                  setMessages={setMessages}
                  castState={castState}
                  setCastState={setCastState}
                  onSetMode={setMode}
                  services={{
                    createEventCastMessage,
                    updateEventCastMessage,
                    toggleEventCastMessageActive,
                    deleteEventCastMessage,
                    upsertEventCastState,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
